import req from '../../api/request'
import type { ImageLike } from '../../utils/assetUrl'
import type {
  CompetitionResult,
  CompetitionResultsPage,
  TeamFinderSearchBody,
} from '../../features/teamFinder/types'

/** One raw entry from GET /finder/teams `data`. */
interface ApiFinderTeam {
  id: string
  team?: {
    id?: string
    name?: string
    name_full?: string
    image?: ImageLike
  } | null
  sport_id?: string | null
  generic_sport_id?: string | null
  is_youth?: boolean | null
  schedule?: {
    id?: string
    name?: string
    name_full?: string
    image?: ImageLike
    starts_at?: string | null
    ends_at?: string | null
    organization?: {
      id?: string
      name?: string
      name_full?: string
    } | null
  } | null
  division?: {
    /** The division's schedule uid (matches `schedule_id` on a registration
     * division, NOT that division's own `id`). */
    id?: string
    name?: string
    name_full?: string
    gender_type_id?: number | null
    /** Available days — string, comma list, array, or a { name } day-of-week
     * object. `available_days` is preferred; day_of_week/nights_of_play are
     * accepted as fallbacks. */
    available_days?: string | string[] | { name?: string | null } | null
    day_of_week?: string | string[] | { name?: string | null } | null
    nights_of_play?: string | string[] | null
    /** Available times of play, same shape as `available_days`. Not shipped by
     * the API yet — accepted here so it renders the moment it appears. */
    available_times?: string | string[] | { name?: string | null } | null
    division_age?: string | null
  } | null
  /** `available_times` may arrive at the entry root rather than on the
   * division; both are accepted. */
  available_times?: string | string[] | { name?: string | null } | null
  positions_needed?: number[] | null
  notes?: string | null
  spots_remaining?: number | null
}

/**
 * Normalize a day/night/time field to a clean list of labels. Accepts a string,
 * a comma-separated string, an array, or a `{ name }` object; empty when
 * absent.
 */
const toLabels = (
  raw?: string | string[] | { name?: string | null } | null
): string[] => {
  if (!raw) {
    return []
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return raw.name ? [raw.name.trim()] : []
  }
  const list = Array.isArray(raw) ? raw : raw.split(',')
  return list.map((n) => n.trim()).filter(Boolean)
}

/** Map one raw finder-teams entry to the client `CompetitionResult`. */
const mapApiTeam = (entry: ApiFinderTeam): CompetitionResult => ({
  id: entry.id,
  scheduleId: entry.schedule?.id ?? undefined,
  teamId: entry.team?.id ?? undefined,
  teamName: entry.team?.name_full || entry.team?.name || '',
  teamImage: entry.team?.image ?? undefined,
  scheduleImage: entry.schedule?.image ?? undefined,
  competitionName: entry.schedule?.name_full || entry.schedule?.name || '',
  organizationId: entry.schedule?.organization?.id ?? undefined,
  organizationName:
    entry.schedule?.organization?.name_full ||
    entry.schedule?.organization?.name ||
    undefined,
  scheduleStartsAt: entry.schedule?.starts_at ?? undefined,
  scheduleEndsAt: entry.schedule?.ends_at ?? undefined,
  sportId: entry.sport_id ?? undefined,
  genericSportId: entry.generic_sport_id ?? undefined,
  divisionId: entry.division?.id ?? undefined,
  divisionName: entry.division?.name_full || entry.division?.name || undefined,
  genderTypeId: entry.division?.gender_type_id ?? undefined,
  isYouth: entry.is_youth ?? undefined,
  // Prefer `available_days`; fall back to day_of_week / nights_of_play.
  availableDays: (() => {
    const d = entry.division
    const days = [d?.available_days, d?.day_of_week, d?.nights_of_play].reduce<
      string[]
    >((acc, field) => (acc.length ? acc : toLabels(field)), [])
    return days.length ? days : undefined
  })(),
  availableTimes: (() => {
    const times = toLabels(
      entry.division?.available_times ?? entry.available_times
    )
    return times.length ? times : undefined
  })(),
  divisionAge: entry.division?.division_age ?? undefined,
  positionsNeeded: entry.positions_needed ?? undefined,
  notes: entry.notes ?? undefined,
  spotsRemaining:
    entry.spots_remaining != null ? entry.spots_remaining : undefined,
})

/**
 * Search for joinable teams via GET /finder/teams. Filters map to query params
 * (all optional); unset filters are omitted upstream by buildSearchBody.
 *
 * Results are paginated: `page` selects the page (the API owns the page size),
 * and `meta.pagination` drives the "Show More" affordance. A response without
 * pagination meta is treated as a single complete page.
 */
export const searchTeamFinder = async (
  params: TeamFinderSearchBody,
  page = 1
): Promise<CompetitionResultsPage> => {
  const response = await req('/finder/teams', { query: { ...params, page } })
  const pagination = response?.meta?.pagination
  return {
    results: ((response?.data ?? []) as ApiFinderTeam[]).map(mapApiTeam),
    currentPage: pagination?.current_page ?? page,
    totalPages: pagination?.total_pages ?? page,
  }
}
